import React, { useState, useEffect, useRef} from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useManualQuery, useMutation } from "graphql-hooks";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

import {
  Box,
  Divider,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  Avatar,
  TextField,
  MenuItem,
} from "@mui/material";
import AlertPopup from "../components/AlertPopup.js";
import placeholderImage from "../assets/r3play-logo-sml.png";
import { styled } from "@mui/material/styles";

import {
  VisibilityOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowDropDown,  
} from "@mui/icons-material";

import {
  ALL_REPORTS_LIST,
  DELETE_REPORT_BY_ID,
} from "../graphQL/report_qry";

const adminURL = process.env.REACT_APP_ADMIN_URL || "/t31u4YYky50K";

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

// UserLink Component
const UserLink = ({ username, userUuid }) => (
  <StyledTooltip title="View User's Page">
    <Link
      to={`/user/${userUuid}/profile`}
      style={{
        color: "#1976d2",
        textDecoration: "none",
        fontSize: "14px",
        display: "inline-block",
        lineHeight: 1,
      }}
      onMouseEnter={(e) => {
        e.target.style.textDecoration = "underline";
      }}
      onMouseLeave={(e) => {
        e.target.style.textDecoration = "none";
      }}
    >
      {username}
    </Link>
  </StyledTooltip>
);

const RootDiv = styled("div")({
  marginRight: "8rem",
  textAlign: "center",
  padding: 2,
  paddingLeft: 0,
  paddingRight: 0,
  width: "1400px",
  //  minWidth: "500px",
});

const ContainerDiv = styled("div")({
    padding: 32,
    marginBottom: "5rem",
    clear: "both",
    borderRadius: "8px",
    minHeight: "870px",
    backgroundColor: "#ffffff",
});

export default function AdminModeration() {
  const [isPaginationMenuOpen, setIsPaginationMenuOpen] = useState(false);
  const paginationSelectRef = useRef(null);
  const paginationMenuRef = useRef(null);
  // Alert popup state
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "",
    reportId: null,
    errorMsg: "",
  });
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState("All");
  const [rows, setRows] = useState([]);

  const [reports, setReports] = useState([]);
  const [getAllReports] = useManualQuery(ALL_REPORTS_LIST);
  const [deleteReport] = useMutation(DELETE_REPORT_BY_ID);

  const getVideoThumbnail = (videoUrl) => {
    if (!videoUrl) return "";

    // YouTube
    if (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")) {
      let videoId = "";
      if (videoUrl.includes("youtu.be/")) {
        videoId = videoUrl.split("youtu.be/")[1].split("?")[0];
      } else if (videoUrl.includes("youtube.com")) {
        const urlParams = new URLSearchParams(videoUrl.split("?")[1]);
        videoId = urlParams.get("v");
      }
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }

    // Vimeo
    if (videoUrl.includes("vimeo.com")) {
      const videoId = videoUrl.split("/").pop().split("?")[0];
      return `https://vumbnail.com/${videoId}.jpg`;
    }

    // Dailymotion
    if (videoUrl.includes("dailymotion.com") || videoUrl.includes("dai.ly")) {
      let videoId = "";
      if (videoUrl.includes("dai.ly/")) {
        videoId = videoUrl.split("dai.ly/")[1].split("?")[0];
      } else {
        videoId = videoUrl.split("/video/")[1].split("?")[0];
      }
      return `https://www.dailymotion.com/thumbnail/video/${videoId}`;
    }

    // Default fallback - return empty or a placeholder
    return "";
  };

  // Get Report data from database
  useEffect(() => {
    allReportsData();
  }, []);

  const allReportsData = async () => {
    const {
      loading: loadingAllReportsData,
      error: errorAllReportsData,
      data: dataAllReportsData,
    } = await getAllReports();
    if (!loadingAllReportsData && dataAllReportsData) {
      console.log(dataAllReportsData);
      const reportsData = Object.values(dataAllReportsData.allReports).map(item => ({
        id: Number(item.reportId),
        uuid: item.uuid,
        objectImage: item.contentType.model !== "video" ?
                    item.objectImage
                    : getVideoThumbnail(item.objectImage) || placeholderImage,
        objectId: item.objectId,
        objectUuid: item.objectUuid,
        contentType: item.contentType,
        reportedBy: item.reportedBy,
        reportedByUserUuid: item.reportedByUserUuid,
        dateReported: item.dateReported,
        objectOwner: item.objectOwner,
        dateResolved: item.dateResolved,
        issue: item.issue,
        note: item.note,
        status: item.status,
        page: item.contentType.model.charAt(0).toUpperCase() + 
              item.contentType.model.slice(1) + " Detail",
        viewUrl: item.contentType.model === "toy" ?
                  `/toy/` + item.objectUuid
                  : item.contentType.model === "story" ?
                  `/story/` + item.objectUuid
                  : `/video/` + item.objectUuid
      }));
      setReports(reportsData);
    }
  };

  // Initialize data
  useEffect(() => {
    setRows(getFilteredData);
  }, [statusFilter, reports]);

  const getFilteredData = () => {
    let filteredData = reports;
    if (statusFilter !== "All") {
      if (statusFilter === "Open") {
        filteredData = filteredData.filter(report => 
          report.status.toLowerCase() === "open");
      } else if (statusFilter === "Under Review") {
        filteredData = filteredData.filter(report => 
          report.status.toLowerCase() === "under review");
      } else if (statusFilter === "Resolved") {
        filteredData = filteredData.filter(
          report => report.status.toLowerCase() === "resolved");
      }
    }
    return filteredData;
  };

  // Status filter change handlers
  const handleStatusFilterChange = event => {
    setStatusFilter(event.target.value);
  };
  
  const dateFormatter = new Intl.DateTimeFormat("en-us", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }); 

  const formatter = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true, // change to false if you prefer 24h format
  });

  const dayFormat = (day) => {
      const dayDate = new Date(day);
      return formatter.format(dayDate);
  };

  const dateFormat = (date) => {
      const dayDate = new Date(date);
      return dateFormatter.format(dayDate);
  };

  const columns = [
    {
      field: "objectImage",
      headerName: "Object Image",
      minWidth: 5,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {        
        return (
          <Box
            sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            }}
            >
            <Avatar
              src={rows.row.objectImage}
              alt={rows.row.objectImage}
              sx={{
                width: 80,
                height: 80,
                borderRadius: "8px",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "dateReported",
      headerName: "Date Reported",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {
        if (rows.row.dateReported === "") return "";        
        return (
          <StyledTooltip title={dayFormat(rows.row.dateReported)} arrow>
            <span
              style={{
                display: "inline-block",
                lineHeight: 1.5,
                verticalAlign: "middle",
              }}
            >
              {dateFormat(rows.row.dateReported)}
            </span>
          </StyledTooltip>
        );
      },
    },
    {
      field: "reportedBy",
      headerName: "Reported By",
      minWidth: 10,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {        
        return (
          <UserLink 
            username={rows.row.reportedBy} 
            userUuid={rows.row.reportedByUserUuid}             
          />
        );
      },
    },
    {
      field: "postedBy",
      headerName: "Posted By",
      minWidth: 10,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {        
        return (
          <UserLink 
            username={rows.row.objectOwner.username} 
            userUuid={rows.row.objectOwner.userUuid} 
          />
        );
      },
    },
    {
      field: "page",
      headerName: "Page",
      minWidth: 5,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {        
        return (
          <Link
              to={rows.row.viewUrl}
              target="_blank"
              style={{
                color: "#1976d2",
                textDecoration: "none",
                fontSize: "14px",
                display: "inline-block",
                lineHeight: 1,
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = "none";
              }}
            >
              {rows.row.page}
            </Link>
        );
      },
    },
    {
      field: "issue",
      headerName: "Issue",
      minWidth: 20,
      flex: 2.5,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 20,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
    },
    {
      field: "dateReviewResolved",
      headerName: "Date Reviewed/Resolved",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {        
        return (
          <StyledTooltip title={rows.row.dateResolved ?
                dateFormat(rows.row.dateResolved)
                : ""} arrow>
            <span
              style={{
                display: "inline-block",
                lineHeight: 1.5,
                verticalAlign: "middle",
              }}
            >
              {rows.row.dateResolved ?
                dateFormat(rows.row.dateResolved)
                : ""}<
            /span>
          </StyledTooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      flex: 1,
      sortable: false,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true,
      renderCell: rows => {        
        return (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',             
            }}
          >
            <StyledTooltip title={`View this ${rows.row.contentType.model}`}>
              <IconButton
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
                onClick={() => window.open(rows.row.viewUrl, "_blank")}
              >
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title="Update this report">
              <IconButton
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
                onClick={() =>
                  navigate(`${adminURL}/report/${rows.row.uuid}`)
                }
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>
            <StyledTooltip title="Delete this report">
              <IconButton
                size="small"
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}                
                onClick={() => handleDeleteClick(rows.row.id)}
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </StyledTooltip>             
          </Box>
        );
      },
    },
  ];

  function DataGridTitle() {
    return (
      <>
        <Grid
          container
          xs={12}
          sx={{
            pt: 2,
            pb: 2,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
        <Typography
          variant="h1"
          sx={{
            fontSize: 20,
            fontWeight: 500,
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            margin: "0 auto",
          }}
        >
          Report List
        </Typography>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            sx={{
              width: "170px",
              paddingRight: 2,
              "& .MuiSelect-select": {
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              },
              "& .MuiOutlinedInput-root": {
                height: "40px",
                backgroundColor: "white",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#666",
                fontSize: "14px",
                "&.Mui-focused": {
                  color: "#666",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
            }}
            SelectProps={{
              IconComponent: ArrowDropDown,
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      justifyContent: "flex-start",
                      textAlign: "left",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Open">Open</MenuItem>
            <MenuItem value="Under Review">Under Review</MenuItem>
            <MenuItem value="Resolved">Resolved</MenuItem>
          </TextField>
        </Grid>
        <Divider />
      </>
    );
  } 
  
  useEffect(() => {
    const handleScroll = (event) => {
      if (isPaginationMenuOpen) {
        const isScrollingInsideDropdown =
          (paginationMenuRef.current &&
            paginationMenuRef.current.contains(event.target)) ||
          (paginationSelectRef.current &&
            paginationSelectRef.current.contains(event.target));

        if (!isScrollingInsideDropdown) {
          setIsPaginationMenuOpen(false);
        }
      }
    };

    if (isPaginationMenuOpen) {
      document.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isPaginationMenuOpen]);
  
  const handleConfirmAction = async () => {
    const reportId = reports.find(item => item.id === alertPopup.reportId)?.id;

    if (alertPopup.type === "delete") {
      // Backend for removing it from database
      const {
        loading: loadingReportDelete,
        error: errorReportDelete,
        data: reportDelete,
      } = await deleteReport({
        variables: {
          reportId: parseInt(reportId),
        },
      });
      if (!loadingReportDelete && reportDelete && reportDelete.deleteReportById.success) {
        // Remove Report from list
        setReports(prevReports => prevReports.filter(report => report.id !== alertPopup.reportId));
        setAlertPopup({ open: false, type: "", reportId: null, errorMsg: "" });
      } else {
        if (reportDelete.deleteReportById.success === false) {
          setAlertPopup({
            open: true,
            type: "error",
            reportId: null,
            errorMsg: reportDelete.deleteReportById.msg,
          });
        } else {
          console.log("errorReportDelete: ", errorReportDelete);
        }
      }
    }
  };

  const handleCancelAction = () => {
    setAlertPopup({ 
      open: false,
      type: "",
      reportId: null,
      errorMsg: "" 
    });
  };

  const handleDeleteClick = reportId => {
    setAlertPopup({
      open: true,
      type: "delete",
      reportId: reportId,
      errorMsg: "",
    });
  };

  const handleEditClick = reportId => {
    setAlertPopup({
      open: true,
      type: "edit",
      reportId: reportId,
      errorMsg: "",
    });
  };

  return (
    <RootDiv>      
      <title>R3PLAY - Report Management – Admin</title>       
      <ContainerDiv>
        <DataGrid
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
              position: "relative",
              zIndex: 1,
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
            // Firefox-specific rendering fix
            "@-moz-document url-prefix()": {
              "& .MuiDataGrid-columnHeaders": {
                transform: "translateY(0.1px)",
              },
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:last-child .MuiDataGrid-columnSeparator":
              {
                display: "none",
              },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: 12,
            },
          }}
          columns={columns}
          rows={rows}
          showCellRightBorder={true}
          slots={{ toolbar: DataGridTitle }}
          getRowId={row => row.id}
          autoHeight={true}
          rowHeight={90}
          experimentalFeatures={{ newEditingApi: true }}
          pagination
          pageSizeOptions={[10, 20, 50]}   // user can select page size
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 10 } }, // default to 10 rows
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          slotProps={{
            pagination: {
              SelectProps: {
                open: isPaginationMenuOpen,
                onOpen: () => setIsPaginationMenuOpen(true),
                onClose: () => setIsPaginationMenuOpen(false),
                ref: paginationSelectRef,
                MenuProps: {
                  PaperProps: {
                    ref: paginationMenuRef,
                  },
                },
              },
            },
          }}
        />

        {/* Alert Popup */}
        <AlertPopup
          open={alertPopup.open}
          onClose={handleCancelAction}
          title={
            alertPopup.type === "edit"
              ? "Edit this Report"
              : alertPopup.type === "error"
              ? "ERROR"
              : "Delete Report"
          }
          message={
            alertPopup.type === "edit"
              ? "TBD"
              : alertPopup.type === "error"
              ? alertPopup.errorMsg
              : "Are you sure you want to delete this report?"
          }
          confirmText={
            alertPopup.type === "edit"
              ? "OK"
              : alertPopup.type === "error"
              ? "OK"
              : "YES, DELETE"
          }
          onConfirm={handleConfirmAction}
        />        
      </ContainerDiv>
    </RootDiv>
  );
}
